import React, { useState, useEffect } from 'react';
import { useApi } from '../../contexts/ApiContext';
import { useToast } from '../../contexts/ToastContext';
import { BarChart2, Settings, AlertTriangle, TrendingUp, Send, CheckCircle2, DollarSign, PackageX, Truck } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell, PieChart, Pie, Legend, ComposedChart, Line } from 'recharts';
import './SmartStock.css';

interface AlertItem {
  sku: string;
  title: string;
  salesLastXDays: number;
  currentStock: number;
  inTransitStock: number;
  minStock: number;
  deficit: number;
  superavit: number;
  hasCustomThreshold: boolean;
  isDemand?: boolean;
  precio: number;
  probabilityDynamic: number;
  status: 'CRITICAL' | 'WARNING' | 'OK';
}

interface DashboardData {
  coverageDays: number;
  baseCoverageDays: number;
  alerts: AlertItem[];
  data: any[];
}

export default function SmartStockDashboard() {
  const { api } = useApi();
  const { showSuccess, showError } = useToast();
  const [activeTab, setActiveTab] = useState<'alerts' | 'config' | 'analytics'>('alerts');
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  
  // Envíos 3PL
  const [envios3PL, setEnvios3PL] = useState<any[]>([]);
  const [selectedEnvioId, setSelectedEnvioId] = useState<string>('');
  const [addingToEnvio, setAddingToEnvio] = useState<string | null>(null);
  const [addingBatch, setAddingBatch] = useState<boolean>(false);
  const [addedSkus, setAddedSkus] = useState<string[]>([]);

  const [sortConfig, setSortConfig] = useState<{ key: keyof AlertItem, direction: 'asc' | 'desc' } | null>(null);
  const [targetDays, setTargetDays] = useState<number>(30);
  const [showAll, setShowAll] = useState<boolean>(false);

  // Configuración
  const [globalCoverage, setGlobalCoverage] = useState<number>(30);
  const [skuThresholds, setSkuThresholds] = useState<{sku: string, min_qty: number, updated_at: string}[]>([]);
  const [newThreshold, setNewThreshold] = useState({ sku: '', minQty: '' });
  const [configLoading, setConfigLoading] = useState(false);

  useEffect(() => {
    loadDashboard();
  }, [targetDays]);

  useEffect(() => {
    loadEnviosMontaje();
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [targetDays, showAll]);

  useEffect(() => {
    if (activeTab === 'config') {
        loadConfig();
    }
  }, [activeTab]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const res = await api.get('/smart-stock/dashboard', { params: { targetDays, showAll } });
      setDashboardData(res.data);
    } catch (error) {
      console.error('Error loading smart stock dashboard:', error);
      showError('Error al cargar datos del dashboard de stock Inteligente');
    } finally {
      setLoading(false);
    }
  };

  const loadEnviosMontaje = async () => {
    try {
      const res = await api.get('/3pl/envios');
      const enviosEnMontaje = res.data.filter((e: any) => e.estado === 'FASE_MONTAJE');
      setEnvios3PL(enviosEnMontaje);
      if (enviosEnMontaje.length > 0) {
          setSelectedEnvioId(enviosEnMontaje[0].id.toString());
      }
    } catch (error) {
        console.error('Error fetching 3pl shipments:', error);
    }
  };

  const handleAddToShipment = async (alert: AlertItem) => {
    if (!selectedEnvioId) {
        showError('Selecciona un envío 3PL de la lista primero.');
        return;
    }

    try {
        setAddingToEnvio(alert.sku);
        await api.post(`/3pl/envios/${selectedEnvioId}/lines`, {
            sku: alert.sku,
            nombre_producto: alert.title,
            cantidad: alert.deficit
        });
        showSuccess(`${alert.deficit}x ${alert.sku} añadidos al envío 3PL`);
        setAddedSkus(prev => [...prev, alert.sku]);
    } catch (error) {
        console.error('Error adding to shipment:', error);
        showError('Error al añadir línea al envío 3PL');
    } finally {
        setAddingToEnvio(null);
    }
  };

  const handleAddByProbability = async (minProbability: number) => {
    if (!selectedEnvioId) {
        showError('Selecciona un envío 3PL de la lista primero.');
        return;
    }

    const matchingAlerts = sortedAlerts.filter(a => a.probabilityDynamic >= minProbability && a.deficit > 0);
    
    if (matchingAlerts.length === 0) {
        showError(`No hay productos con >=${minProbability}% de probabilidad o déficit pendiente en la lista actual.`);
        return;
    }

    if (!window.confirm(`¿Estás seguro de añadir ${matchingAlerts.length} productos (>=${minProbability}% prob.) al envío 3PL actual de forma automática?`)) {
        return;
    }

    setAddingBatch(true);
    let successCount = 0;
    let failedCount = 0;

    for (const alert of matchingAlerts) {
        try {
            await api.post(`/3pl/envios/${selectedEnvioId}/lines`, {
                sku: alert.sku,
                nombre_producto: alert.title,
                cantidad: alert.deficit
            });
            setAddedSkus(prev => [...prev, alert.sku]);
            successCount++;
        } catch (error) {
            console.error(`Error procesando SKU ${alert.sku}:`, error);
            failedCount++;
        }
    }

    setAddingBatch(false);
    
    if (failedCount === 0) {
        showSuccess(`¡${successCount} productos añadidos al envío de forma automática!`);
    } else {
        showError(`Se añadieron ${successCount} productos. Fallaron ${failedCount}. Revisa consola.`);
    }
  };

  const loadConfig = async () => {
    try {
        setConfigLoading(true);
        const [configRes, thresholdsRes] = await Promise.all([
            api.get('/smart-stock/config'),
            api.get('/smart-stock/thresholds')
        ]);
        setGlobalCoverage(configRes.data.globalCoverageDays);
        setSkuThresholds(thresholdsRes.data);
    } catch (error) {
        console.error('Error loading config:', error);
        showError('No se pudo cargar la configuración');
    } finally {
        setConfigLoading(false);
    }
  };

  const handleSaveGlobalConfig = async () => {
      try {
          await api.put('/smart-stock/config', { globalCoverageDays: globalCoverage });
          showSuccess('Configuración global actualizada');
          loadDashboard(); // Refrescar los datos base
      } catch (error) {
          showError('Error guardando la configuración');
      }
  };

  const handleAddSkuThreshold = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!newThreshold.sku || !newThreshold.minQty) return;

      try {
          await api.post('/smart-stock/thresholds', { 
              sku: newThreshold.sku.trim().toUpperCase(), 
              minQty: parseInt(newThreshold.minQty) 
          });
          showSuccess('Umbral manual añadido con éxito');
          setNewThreshold({ sku: '', minQty: '' });
          loadConfig(); // Refresh table
          loadDashboard();
      } catch (error) {
          showError('Error añadiendo el umbral específico');
      }
  };

  const handleDeleteSkuThreshold = async (sku: string) => {
      try {
          await api.delete(`/smart-stock/thresholds/${sku}`);
          showSuccess(`Umbral eliminado para ${sku}`);
          loadConfig();
          loadDashboard();
      } catch (error) {
          showError('Error eliminando el umbral');
      }
  };

  const requestSort = (key: keyof AlertItem) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedAlerts = React.useMemo(() => {
    const listToMap = showAll ? dashboardData?.data : dashboardData?.alerts;
    if (!listToMap) return [];
    
    // Filtramos para asegurar que no se mapean duplicados si hay un error en los datos
    const sortableAlerts = Array.from(new Map(listToMap.map((item: any) => [item.sku, item])).values()) as AlertItem[];
    if (sortConfig !== null) {
      sortableAlerts.sort((a, b) => {
        const valA = a[sortConfig.key] ?? '';
        const valB = b[sortConfig.key] ?? '';
        if (valA < valB) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (valA > valB) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableAlerts.filter(alert => !addedSkus.includes(alert.sku));
  }, [dashboardData, sortConfig, showAll, addedSkus]);

  const renderTabs = () => (
    <div className='smart-stock-tabs'>
      <button 
        className={`tab-btn ${activeTab === 'alerts' ? 'active' : ''}`}
        onClick={() => setActiveTab('alerts')}
      >
        <AlertTriangle size={18} /> Alertas y Reposición
      </button>
      <button 
        className={`tab-btn ${activeTab === 'config' ? 'active' : ''}`}
        onClick={() => setActiveTab('config')}
      >
        <Settings size={18} /> Configuración de Umbrales
      </button>
      <button 
        className={`tab-btn ${activeTab === 'analytics' ? 'active' : ''}`}
        onClick={() => setActiveTab('analytics')}
      >
        <BarChart2 size={18} /> Analítica
      </button>
    </div>
  );

  return (
    <div className='smart-stock-container fade-in'>
      <header className='smart-stock-header'>
        <div>
          <h2>Control Inteligente de Stock (Amphora USA)</h2>
          <p>Cruza ventas de Shopify con stock físico para calcular necesidades de reposición dinámicas.</p>
        </div>
      </header>

      {renderTabs()}

      <div className='smart-stock-content'>
        {loading ? (
          <div className='loading-spinner'>Cargando datos del dashboard...</div>
        ) : (
          <>
            {activeTab === 'alerts' && (
              <div className='alerts-panel'>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
                    <div>
                        <h3>{showAll ? 'Inventario Completo (Amphora)' : 'Productos en Déficit'}</h3>
                        <p style={{ color: 'var(--text-secondary)' }}>Calculado en base a {dashboardData?.baseCoverageDays || 30} días de velocidad proyectados para {dashboardData?.coverageDays || 30} días de cobertura.</p>
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--bg-primary)', padding: '12px', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                        <span style={{ fontSize: '14px', fontWeight: 500 }}>Días Cobertura:</span>
                        <select 
                            value={targetDays} 
                            onChange={(e) => setTargetDays(Number(e.target.value))}
                            style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', fontWeight: 'bold' }}
                        >
                            <option value={30}>30 Días (Base)</option>
                            <option value={60}>60 Días</option>
                            <option value={90}>90 Días</option>
                            <option value={120}>120 Días</option>
                        </select>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--bg-primary)', padding: '12px', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '14px', fontWeight: 500 }}>Ver Todos:</span>
                            <div 
                                style={{ 
                                    width: '36px', 
                                    height: '20px', 
                                    background: showAll ? 'var(--accent)' : 'var(--border-color)', 
                                    borderRadius: '10px', 
                                    position: 'relative', 
                                    cursor: 'pointer',
                                    transition: 'background 0.3s'
                                }}
                                onClick={() => setShowAll(!showAll)}
                            >
                                <div style={{ 
                                    width: '16px', 
                                    height: '16px', 
                                    background: 'white', 
                                    borderRadius: '50%', 
                                    position: 'absolute', 
                                    top: '2px', 
                                    left: showAll ? '18px' : '2px', 
                                    transition: 'left 0.3s' 
                                }} />
                            </div>
                        </div>
                    </div>

                    {envios3PL.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--bg-primary)', padding: '12px', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                            <span style={{ fontSize: '14px', fontWeight: 500 }}>Envío 3PL Destino:</span>
                            <select 
                                value={selectedEnvioId} 
                                onChange={(e) => setSelectedEnvioId(e.target.value)}
                                style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid var(--border-color)' }}
                            >
                                {envios3PL.map(envio => (
                                    <option key={envio.id} value={envio.id}>{envio.codigo}</option>
                                ))}
                            </select>
                            {[45, 25, 15].map(prob => (
                                <button
                                    key={prob}
                                    onClick={() => handleAddByProbability(prob)}
                                    disabled={addingBatch || !selectedEnvioId}
                                    style={{
                                        padding: '8px 16px',
                                        background: (!selectedEnvioId || addingBatch) ? '#e5e7eb' : prob === 45 ? '#10b981' : prob === 25 ? '#f59e0b' : '#ef4444',
                                        color: (!selectedEnvioId || addingBatch) ? '#9ca3af' : 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: (!selectedEnvioId || addingBatch) ? 'not-allowed' : 'pointer',
                                        fontWeight: 600,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        marginLeft: '8px'
                                    }}
                                    title={`Añadirá todos los productos que tengan Déficit y Prob. de Venta >= ${prob}%`}
                                >
                                    <Send size={16} />
                                    {addingBatch ? 'Añadiendo...' : `>=${prob}% Prob.`}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {dashboardData?.alerts && dashboardData.alerts.length > 0 ? (
                    <div className='table-container'>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid var(--border-color)', textAlign: 'left' }}>
                                    <th 
                                        style={{ padding: '12px', color: 'var(--text-secondary)', cursor: 'pointer', userSelect: 'none' }}
                                        onClick={() => requestSort('sku')}
                                        title="Ordenar por SKU"
                                    >
                                        SKU / Producto {sortConfig?.key === 'sku' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                                    </th>
                                    <th 
                                        style={{ padding: '12px', color: 'var(--text-secondary)', textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                                        onClick={() => requestSort('salesLastXDays')}
                                        title="Ordenar por Ventas"
                                    >
                                        Ventas base ({dashboardData.baseCoverageDays || 30}d) {sortConfig?.key === 'salesLastXDays' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                                    </th>
                                    <th 
                                        style={{ padding: '12px', color: 'var(--text-secondary)', textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                                        onClick={() => requestSort('probabilityDynamic')}
                                        title={`Probabilidad histórica (últimos 6 meses) de vender al menos 1 unidad en ${targetDays} días`}
                                    >
                                        Prob. Venta {targetDays}d {sortConfig?.key === 'probabilityDynamic' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                                    </th>
                                    <th 
                                        style={{ padding: '12px', color: 'var(--text-secondary)', textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                                        onClick={() => requestSort('minStock')}
                                        title="Ordenar por Requisito Mínimo Calculado"
                                    >
                                        Requisito ({dashboardData.coverageDays || 30}d) {sortConfig?.key === 'minStock' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                                    </th>
                                    <th 
                                        style={{ padding: '12px', color: 'var(--text-secondary)', textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                                        onClick={() => requestSort('currentStock')}
                                        title="Ordenar por Stock Actual"
                                    >
                                        Stock Amphora {sortConfig?.key === 'currentStock' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                                    </th>
                                    <th 
                                        style={{ padding: '12px', color: 'var(--text-secondary)', textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                                        onClick={() => requestSort('inTransitStock')}
                                        title="Ordenar por Envío en Camino (3PL)"
                                    >
                                        En Camino {sortConfig?.key === 'inTransitStock' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                                    </th>
                                    <th 
                                        style={{ padding: '12px', color: 'var(--text-secondary)', textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                                        onClick={() => requestSort('deficit')}
                                        title="Ordenar por Déficit"
                                    >
                                        Déficit {sortConfig?.key === 'deficit' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                                    </th>
                                    <th 
                                        style={{ padding: '12px', color: 'var(--text-secondary)', textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                                        onClick={() => requestSort('superavit')}
                                        title="Ordenar por Superávit"
                                    >
                                        Superávit {sortConfig?.key === 'superavit' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                                    </th>
                                    <th style={{ padding: '12px', color: 'var(--text-secondary)', textAlign: 'right' }}>Acción</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedAlerts.map((alert) => (
                                    <tr key={alert.sku} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                        <td style={{ padding: '16px 12px' }}>
                                            <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{alert.sku}</div>
                                            <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                                                {alert.title} 
                                                {alert.hasCustomThreshold && <span style={{ color: 'var(--accent)', marginLeft: '4px' }} title='Regla manual'>(★)</span>}
                                                {alert.isDemand && (
                                                    <span style={{ 
                                                        marginLeft: '6px', 
                                                        padding: '2px 6px', 
                                                        background: '#e0f2fe', 
                                                        border: '1px solid #bae6fd', 
                                                        borderRadius: '4px', 
                                                        fontSize: '10px', 
                                                        fontWeight: 600, 
                                                        color: '#0369a1' 
                                                    }}>
                                                        BAJO DEMANDA
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 500 }}>
                                            {alert.salesLastXDays}
                                        </td>
                                        <td style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 500 }}>
                                            <span style={{ 
                                                color: alert.probabilityDynamic >= 80 ? '#059669' : alert.probabilityDynamic >= 40 ? '#d97706' : '#dc2626',
                                                background: alert.probabilityDynamic >= 80 ? '#d1fae5' : alert.probabilityDynamic >= 40 ? '#fef3c7' : '#fee2e2',
                                                padding: '4px 8px',
                                                borderRadius: '4px',
                                                fontSize: '13px'
                                            }} title="Probabilidad calculada usando los últimos 6 meses">
                                                {alert.probabilityDynamic}%
                                            </span>
                                        </td>
                                        <td style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 500, color: 'var(--accent)' }}>
                                            {alert.minStock}
                                        </td>
                                        <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                                            <span style={{ 
                                                color: alert.currentStock <= 0 ? '#ef4444' : 'var(--text-primary)',
                                                fontWeight: alert.currentStock <= 0 ? 'bold' : 'normal'
                                            }}>
                                                {alert.currentStock}
                                            </span>
                                        </td>
                                        <td style={{ padding: '16px 12px', textAlign: 'center', fontWeight: alert.inTransitStock > 0 ? 600 : 400, color: alert.inTransitStock > 0 ? '#3b82f6' : 'var(--text-tertiary)' }}>
                                            {alert.inTransitStock > 0 ? `+${alert.inTransitStock}` : '0'}
                                        </td>
                                        <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                                            <div style={{ 
                                                display: 'inline-block',
                                                padding: '4px 12px',
                                                borderRadius: '20px',
                                                background: alert.status === 'CRITICAL' ? '#fef2f2' : (alert.deficit > 0 ? '#fffbeb' : 'transparent'),
                                                color: alert.status === 'CRITICAL' ? '#991b1b' : (alert.deficit > 0 ? '#92400e' : 'var(--text-tertiary)'),
                                                fontWeight: alert.deficit > 0 ? 'bold' : 'normal',
                                                fontSize: '14px'
                                            }}>
                                                {alert.deficit > 0 ? `-${alert.deficit}` : '0'}
                                            </div>
                                        </td>
                                        <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                                            <div style={{ 
                                                display: 'inline-block',
                                                padding: '4px 12px',
                                                borderRadius: '20px',
                                                background: alert.superavit > 0 ? '#ecfdf5' : 'transparent',
                                                color: alert.superavit > 0 ? '#065f46' : 'var(--text-tertiary)',
                                                fontWeight: alert.superavit > 0 ? 'bold' : 'normal',
                                                fontSize: '14px'
                                            }}>
                                                {alert.superavit > 0 ? `+${alert.superavit}` : '0'}
                                            </div>
                                        </td>
                                        <td style={{ padding: '16px 12px', textAlign: 'right' }}>
                                            <button 
                                                onClick={() => handleAddToShipment(alert)}
                                                disabled={addingToEnvio === alert.sku || !selectedEnvioId}
                                                title={!selectedEnvioId ? "No hay envíos 3PL en fase de montaje disponibles" : "Añadir al envío seleccionado"}
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    padding: '8px 16px',
                                                    background: (!selectedEnvioId || addingToEnvio === alert.sku) ? '#e5e7eb' : 'var(--accent)',
                                                    color: (!selectedEnvioId || addingToEnvio === alert.sku) ? '#9ca3af' : 'white',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    cursor: (!selectedEnvioId || addingToEnvio === alert.sku) ? 'not-allowed' : 'pointer',
                                                    fontSize: '13px',
                                                    fontWeight: 500,
                                                    transition: 'all 0.2s'
                                                }}
                                            >
                                                {addingToEnvio === alert.sku ? (
                                                    'Enviando...'
                                                ) : (
                                                    <>
                                                        <Send size={14} /> Añadir ({alert.deficit}) a 3PL
                                                    </>
                                                )}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot style={{ background: 'var(--bg-tertiary)', borderTop: '2px solid var(--border-color)' }}>
                                <tr>
                                    <td style={{ padding: '16px 12px', fontWeight: 'bold', color: 'var(--text-primary)', textAlign: 'right' }}>
                                        TOTALES:
                                    </td>
                                    <td style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                                        {sortedAlerts.reduce((sum, a) => sum + a.salesLastXDays, 0)}
                                    </td>
                                    <td style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 'bold', color: 'var(--accent)' }}>
                                        {sortedAlerts.reduce((sum, a) => sum + a.minStock, 0)}
                                    </td>
                                    <td style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                                        {sortedAlerts.reduce((sum, a) => sum + a.currentStock, 0)}
                                    </td>
                                    <td style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 'bold', color: '#3b82f6' }}>
                                        {sortedAlerts.reduce((sum, a) => sum + (a.inTransitStock || 0), 0)}
                                    </td>
                                    <td style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 'bold', color: '#991b1b' }}>
                                        -{sortedAlerts.reduce((sum, a) => sum + a.deficit, 0)}
                                    </td>
                                    <td style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 'bold', color: '#065f46' }}>
                                        +{sortedAlerts.reduce((sum, a) => sum + (a.superavit || 0), 0)}
                                    </td>
                                    <td style={{ padding: '16px 12px' }}></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                ) : (
                    <div className='empty-state'>
                        <CheckCircle2 size={48} className='mx-auto mb-4 text-green-500' style={{ color: '#10b981' }} />
                        <h3 style={{ fontSize: '18px', color: 'var(--text-primary)', marginBottom: '8px' }}>¡Todo al día!</h3>
                        <p>No se encontraron productos en déficit basándonos en las reglas actuales.</p>
                    </div>
                )}
              </div>
            )}
            
            {activeTab === 'config' && (
              <div className='config-panel'>
                <div style={{ background: 'white', padding: '24px', borderRadius: '12px', border: '1px solid var(--border-color)', marginBottom: '24px' }}>
                  <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', color: 'var(--text-primary)' }}>1. Piloto Automático (Regla Global)</h3>
                  <p style={{ color: 'var(--text-secondary)', marginBottom: '20px', fontSize: '14px' }}>
                    Esta regla aplica a <strong>todos los productos por defecto</strong>. El sistema leerá el ritmo de ventas de cada SKU y se asegurará de mantener en Amphor suficientes unidades para cubrir los días que especifiques aquí.
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', background: 'var(--bg-tertiary)', padding: '16px', borderRadius: '8px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '8px' }}>
                        Días de Cobertura Deseados
                      </label>
                      <input 
                        type="number" 
                        min="1"
                        value={globalCoverage}
                        onChange={(e) => setGlobalCoverage(Number(e.target.value))}
                        style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '120px', fontSize: '16px', fontWeight: 'bold' }}
                      />
                    </div>
                    <button 
                      onClick={handleSaveGlobalConfig}
                      style={{ marginTop: '24px', padding: '10px 20px', background: 'var(--accent)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}
                    >
                      Guardar Regla Global
                    </button>
                  </div>
                </div>

                <div style={{ background: 'white', padding: '24px', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
                  <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', color: 'var(--text-primary)' }}>2. Excepciones Manuales por SKU</h3>
                  <p style={{ color: 'var(--text-secondary)', marginBottom: '20px', fontSize: '14px' }}>
                    Añade productos para <strong>forzar un umbral mínimo fijo</strong> de unidades, ignorando la regla global del piloto automático. Ideal para piezas nuevas sin historial de venta o estrategias puntuales.
                  </p>
                  
                  <form onSubmit={handleAddSkuThreshold} style={{ display: 'flex', gap: '16px', marginBottom: '24px', alignItems: 'flex-end', background: 'var(--bg-tertiary)', padding: '16px', borderRadius: '8px' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '8px' }}>SKU</label>
                      <input 
                        type="text" 
                        value={newThreshold.sku}
                        onChange={(e) => setNewThreshold({...newThreshold, sku: e.target.value.toUpperCase()})}
                        placeholder="Ej: AN-01-M"
                        style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border-color)' }}
                        required
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '8px' }}>Cantidad Mínima</label>
                      <input 
                        type="number" 
                        min="0"
                        value={newThreshold.minQty}
                        onChange={(e) => setNewThreshold({...newThreshold, minQty: e.target.value})}
                        placeholder="Ej: 100"
                        style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border-color)' }}
                        required
                      />
                    </div>
                    <button 
                      type="submit"
                      style={{ padding: '10px 20px', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}
                    >
                      Añadir Excepción
                    </button>
                  </form>

                  {configLoading ? (
                      <p>Cargando tabla de excepciones...</p>
                  ) : (
                      <div className='table-container'>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid var(--border-color)', textAlign: 'left', background: 'var(--bg-tertiary)' }}>
                                    <th style={{ padding: '12px', color: 'var(--text-secondary)' }}>SKU</th>
                                    <th style={{ padding: '12px', color: 'var(--text-secondary)' }}>Mínimo Fijo (Unidades)</th>
                                    <th style={{ padding: '12px', color: 'var(--text-secondary)', textAlign: 'right' }}>Acción</th>
                                </tr>
                            </thead>
                            <tbody>
                                {skuThresholds.length === 0 ? (
                                    <tr>
                                        <td colSpan={3} style={{ padding: '24px', textAlign: 'center', color: 'var(--text-tertiary)' }}>
                                            No hay excepciones manuales configuradas. Todos los SKU usan el Piloto Automático.
                                        </td>
                                    </tr>
                                ) : (
                                    skuThresholds.map((t) => (
                                        <tr key={t.sku} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                            <td style={{ padding: '12px', fontWeight: 600, color: 'var(--text-primary)' }}>{t.sku}</td>
                                            <td style={{ padding: '12px', fontWeight: 'bold' }}>{t.min_qty}</td>
                                            <td style={{ padding: '12px', textAlign: 'right' }}>
                                                <button 
                                                    onClick={() => handleDeleteSkuThreshold(t.sku)}
                                                    style={{ padding: '6px 12px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: 500 }}
                                                >
                                                    Eliminar Excepción
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                      </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'analytics' && (
              <div className='analytics-panel' style={{ padding: '20px 0' }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                  gap: '20px',
                  marginBottom: '30px'
                }}>
                  {/* Tarjetas KPI Monetarias */}
                  <div style={{ background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid var(--border-color)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                      <h4 style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px', fontWeight: 500 }}>Capital Inmovilizado</h4>
                      <div style={{ padding: '8px', background: '#ecfdf5', borderRadius: '8px', color: '#059669' }}>
                        <DollarSign size={20} />
                      </div>
                    </div>
                    <div style={{ fontSize: '28px', fontWeight: 700, color: 'var(--text-primary)' }}>
                      ${(dashboardData?.data || []).reduce((acc, current) => acc + (current.currentStock * current.precio), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
                      Valor del stock físico disponible
                    </div>
                  </div>

                  <div style={{ background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid var(--border-color)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                      <h4 style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px', fontWeight: 500 }}>Costo de Oportunidad</h4>
                      <div style={{ padding: '8px', background: '#fef2f2', borderRadius: '8px', color: '#dc2626' }}>
                        <PackageX size={20} />
                      </div>
                    </div>
                    <div style={{ fontSize: '28px', fontWeight: 700, color: '#dc2626' }}>
                      ${(dashboardData?.data || []).reduce((acc, current) => acc + (current.deficit * current.precio), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
                      Ventas perdidas por falta de stock
                    </div>
                  </div>

                  <div style={{ background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid var(--border-color)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                      <h4 style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px', fontWeight: 500 }}>Valor del Exceso</h4>
                      <div style={{ padding: '8px', background: '#fffbeb', borderRadius: '8px', color: '#d97706' }}>
                        <AlertTriangle size={20} />
                      </div>
                    </div>
                    <div style={{ fontSize: '28px', fontWeight: 700, color: '#d97706' }}>
                      ${(dashboardData?.data || []).reduce((acc, current) => acc + (current.superavit * current.precio), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
                      Capital por encima de lo óptimo
                    </div>
                  </div>

                  <div style={{ background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid var(--border-color)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                      <h4 style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px', fontWeight: 500 }}>Capital en Tránsito</h4>
                      <div style={{ padding: '8px', background: '#eff6ff', borderRadius: '8px', color: '#3b82f6' }}>
                        <Truck size={20} />
                      </div>
                    </div>
                    <div style={{ fontSize: '28px', fontWeight: 700, color: '#3b82f6' }}>
                      ${(dashboardData?.data || []).reduce((acc, current) => acc + (current.inTransitStock * current.precio), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
                      Stock en paquetes 3PL o volando
                    </div>
                  </div>
                </div>

                {/* Gráficos de Top 10 */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
                  gap: '20px'
                }}>
                  {/* Top 10 Capital Inmovilizado */}
                  <div style={{ background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid var(--border-color)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                    <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', color: 'var(--text-primary)' }}>Top 10: Mayor Capital Inmovilizado</h3>
                    <div style={{ height: '400px', width: '100%' }}>
                      <ResponsiveContainer>
                        <BarChart
                          data={[...(dashboardData?.data || [])]
                            .map(item => ({ name: item.sku, value: item.currentStock * item.precio }))
                            .sort((a, b) => b.value - a.value)
                            .slice(0, 10)}
                          layout="vertical"
                          margin={{ top: 5, right: 30, left: 40, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                          <XAxis type="number" tickFormatter={(value) => `$${value}`} />
                          <YAxis dataKey="name" type="category" width={80} style={{ fontSize: '12px' }} />
                          <Tooltip 
                            formatter={(value: number) => [`$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 'Capital']}
                            contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                          />
                          <Bar dataKey="value" fill="#10b981" radius={[0, 4, 4, 0]}>
                            {
                              [...(dashboardData?.data || [])]
                                .map(item => ({ name: item.sku, value: item.currentStock * item.precio }))
                                .sort((a, b) => b.value - a.value)
                                .slice(0, 10)
                                .map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={'#10b981'} />
                                ))
                            }
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Top 10 Déficit o Ventas Perdidas */}
                  <div style={{ background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid var(--border-color)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                    <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', color: 'var(--text-primary)' }}>Top 10: Mayor Costo de Oportunidad (Déficit)</h3>
                    <div style={{ height: '400px', width: '100%' }}>
                      <ResponsiveContainer>
                        <BarChart
                          data={[...(dashboardData?.data || [])]
                            .map(item => ({ name: item.sku, value: item.deficit * item.precio }))
                            .filter(item => item.value > 0)
                            .sort((a, b) => b.value - a.value)
                            .slice(0, 10)}
                          layout="vertical"
                          margin={{ top: 5, right: 30, left: 40, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                          <XAxis type="number" tickFormatter={(value) => `$${value}`} />
                          <YAxis dataKey="name" type="category" width={80} style={{ fontSize: '12px' }} />
                          <Tooltip 
                            formatter={(value: number) => [`$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 'Oportunidad Perdida']}
                            contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                          />
                          <Bar dataKey="value" fill="#ef4444" radius={[0, 4, 4, 0]}>
                            {
                              [...(dashboardData?.data || [])]
                                .map(item => ({ name: item.sku, value: item.deficit * item.precio }))
                                .filter(item => item.value > 0)
                                .sort((a, b) => b.value - a.value)
                                .slice(0, 10)
                                .map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={'#ef4444'} />
                                ))
                            }
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                {/* Gráficos Adicionales y de Distribución */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
                  gap: '20px',
                  marginTop: '20px'
                }}>
                  {/* Salud del Inventario (Donut) */}
                  <div style={{ background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid var(--border-color)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                    <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', color: 'var(--text-primary)' }}>Distribución del Capital según Estado</h3>
                    <div style={{ height: '350px', width: '100%' }}>
                      <ResponsiveContainer>
                        <PieChart>
                          <Pie
                            data={[
                              { name: 'Saludable (OK)', value: (dashboardData?.data || []).filter(d => d.status === 'OK').reduce((a, b) => a + (b.currentStock * b.precio), 0), color: '#10b981' },
                              { name: 'Riesgo (WARNING)', value: (dashboardData?.data || []).filter(d => d.status === 'WARNING').reduce((a, b) => a + (b.currentStock * b.precio), 0), color: '#f59e0b' },
                              { name: 'Agotado (CRITICAL)', value: (dashboardData?.data || []).filter(d => d.status === 'CRITICAL').reduce((a, b) => a + (b.currentStock * b.precio), 0), color: '#ef4444' }
                            ].filter(d => d.value > 0)}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={70}
                            outerRadius={110}
                            paddingAngle={2}
                          >
                            {
                              [
                                { name: 'Saludable (OK)', value: (dashboardData?.data || []).filter(d => d.status === 'OK').reduce((a, b) => a + (b.currentStock * b.precio), 0), color: '#10b981' },
                                { name: 'Riesgo (WARNING)', value: (dashboardData?.data || []).filter(d => d.status === 'WARNING').reduce((a, b) => a + (b.currentStock * b.precio), 0), color: '#f59e0b' },
                                { name: 'Agotado (CRITICAL)', value: (dashboardData?.data || []).filter(d => d.status === 'CRITICAL').reduce((a, b) => a + (b.currentStock * b.precio), 0), color: '#ef4444' }
                              ].filter(d => d.value > 0).map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))
                            }
                          </Pie>
                          <Tooltip 
                            formatter={(value: number) => [`$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 'Capital']}
                            contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                          />
                          <Legend verticalAlign="bottom" height={36}/>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Capital por Categoría (Pie) */}
                  <div style={{ background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid var(--border-color)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                    <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', color: 'var(--text-primary)' }}>Concentración de Capital por Categoría</h3>
                    <div style={{ height: '350px', width: '100%' }}>
                      <ResponsiveContainer>
                        <PieChart>
                          <Pie
                            data={Object.entries(
                              (dashboardData?.data || []).reduce((acc: any, item: any) => {
                                const t = (item.title || '').toLowerCase();
                                const s = (item.sku || '').toLowerCase();
                                let cat = 'Otros';
                                if (t.includes('anillo') || t.includes('ring') || s.startsWith('an-')) cat = 'Anillos';
                                else if (t.includes('pulsera') || t.includes('bracelet') || s.startsWith('pu-') || s.startsWith('pl-')) cat = 'Pulseras';
                                else if (t.includes('collar') || t.includes('necklace') || s.startsWith('co-')) cat = 'Collares';
                                else if (t.includes('colgante') || t.includes('pendant') || s.startsWith('cg-')) cat = 'Colgantes';
                                
                                acc[cat] = (acc[cat] || 0) + (item.currentStock * item.precio);
                                return acc;
                              }, {})
                            ).map(([name, value]) => ({ name, value })).filter((d: any) => d.value > 0).sort((a: any, b: any) => b.value - a.value)}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={110}
                          >
                            {
                              Object.entries(
                                (dashboardData?.data || []).reduce((acc: any, item: any) => {
                                  const t = (item.title || '').toLowerCase();
                                  let cat = 'Otros';
                                  if (t.includes('anillo') || t.includes('ring')) cat = 'Anillos';
                                  else if (t.includes('pulsera') || t.includes('bracelet')) cat = 'Pulseras';
                                  else if (t.includes('collar') || t.includes('necklace')) cat = 'Collares';
                                  else if (t.includes('colgante') || t.includes('pendant')) cat = 'Colgantes';
                                  acc[cat] = (acc[cat] || 0) + (item.currentStock * item.precio);
                                  return acc;
                                }, {})
                              ).map(([name, value]) => ({ name, value })).filter((d: any) => d.value > 0).sort((a: any, b: any) => b.value - a.value).map((entry, index) => {
                                const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6', '#64748b'];
                                return <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />;
                              })
                            }
                          </Pie>
                          <Tooltip 
                            formatter={(value: number) => [`$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 'Capital']}
                            contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                          />
                          <Legend verticalAlign="bottom" height={36}/>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                {/* Distribución de Probabilidades de Venta */}
                <div style={{ background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid var(--border-color)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', marginTop: '20px' }}>
                  <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', color: 'var(--text-primary)' }}>Distribución del Inventario por Probabilidad de Venta (Próximos {targetDays}d)</h3>
                  <div style={{ height: '350px', width: '100%' }}>
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Alta Probabilidad (>80%)', value: (dashboardData?.data || []).filter((d: any) => d.probabilityDynamic >= 80).length, color: '#10b981' },
                            { name: 'Media Probabilidad (40-80%)', value: (dashboardData?.data || []).filter((d: any) => d.probabilityDynamic >= 40 && d.probabilityDynamic < 80).length, color: '#f59e0b' },
                            { name: 'Baja Probabilidad (<40%)', value: (dashboardData?.data || []).filter((d: any) => d.probabilityDynamic < 40).length, color: '#ef4444' }
                          ].filter(d => d.value > 0)}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={110}
                          label={({ name, percent }: any) => `${name?.split(' ')[0] || ''} ${((percent || 0) * 100).toFixed(0)}%`}
                        >
                          {
                            [
                              { name: 'Alta Probabilidad (>80%)', value: (dashboardData?.data || []).filter((d: any) => d.probabilityDynamic >= 80).length, color: '#10b981' },
                              { name: 'Media Probabilidad (40-80%)', value: (dashboardData?.data || []).filter((d: any) => d.probabilityDynamic >= 40 && d.probabilityDynamic < 80).length, color: '#f59e0b' },
                              { name: 'Baja Probabilidad (<40%)', value: (dashboardData?.data || []).filter((d: any) => d.probabilityDynamic < 40).length, color: '#ef4444' }
                            ].filter(d => d.value > 0).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))
                          }
                        </Pie>
                        <Tooltip 
                          formatter={(value: number) => [value, 'SKUs']}
                          contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                        />
                        <Legend verticalAlign="bottom" height={36}/>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Composed Chart: Ventas vs Stock Físico (Top 15 Bestsellers) */}
                <div style={{ background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid var(--border-color)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', marginTop: '20px' }}>
                  <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', color: 'var(--text-primary)' }}>Top 15 Bestsellers: Ritmo de Venta vs Existencias Reales (Unidades)</h3>
                  <div style={{ height: '400px', width: '100%' }}>
                    <ResponsiveContainer>
                      <ComposedChart
                        data={[...(dashboardData?.data || [])]
                          .sort((a, b) => b.salesLastXDays - a.salesLastXDays)
                          .slice(0, 15)}
                        margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                      >
                        <CartesianGrid stroke="#f5f5f5" strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="sku" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={60} />
                        <YAxis yAxisId="left" orientation="left" stroke="#3b82f6" />
                        <YAxis yAxisId="right" orientation="right" stroke="#f59e0b" />
                        <Tooltip 
                          contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                        />
                        <Legend />
                        <Bar yAxisId="left" dataKey="salesLastXDays" name={`Ventas (${dashboardData?.coverageDays || 30} días)`} barSize={24} fill="#3b82f6" radius={[4, 4, 0, 0]} />
                        <Line yAxisId="right" type="monotone" dataKey="currentStock" name="Stock Físico" stroke="#f59e0b" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </div>

              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
